import { useEffect, useState } from 'react';
import { itemWord, plural } from '../plural';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ShopError, api, type CustomerOrderSummary, type LoyaltyBalance } from '../api';
import { useCustomer } from '../auth/CustomerContext';
import { formatCzk } from '../money';
import { usePageMeta } from '../seo';

/**
 * „Můj účet" – profil a historie objednávek.
 *
 * Historie se napojuje přes /api/shop/my-orders (jen objednávky přihlášeného
 * zákazníka). Každou lze rozkliknout na sledování – detail (položky, cesta,
 * faktura) dotáhne existující stránka sledování, aby zákazník viděl přesně
 * to, co u „Kde je moje objednávka".
 */
export function AccountPage() {
  usePageMeta({ title: 'Můj účet | Super Krmivo', index: false });
  const { customer, loading, logout } = useCustomer();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<readonly CustomerOrderSummary[] | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  /**
   * VĚRNOSTNÍ BODY (#40). Zůstatek chodí ze serveru včetně kurzu, takže
   * korunová hodnota se tu nikde nehádá. Výpadek účet neshodí — bez zůstatku
   * se sekce prostě nevykreslí, objednávky i profil fungují dál.
   */
  const [loyalty, setLoyalty] = useState<LoyaltyBalance | null>(null);

  useEffect(() => {
    if (!customer) return;
    let alive = true;
    api
      .myOrders()
      .then((result) => {
        if (alive) setOrders(result.orders);
      })
      .catch((caught: unknown) => {
        if (!alive) return;
        setOrders([]);
        setOrdersError(
          caught instanceof ShopError ? caught.message : 'Objednávky se nepodařilo načíst.',
        );
      });
    return () => {
      alive = false;
    };
  }, [customer]);

  // Zůstatek bodů. Vlastní požadavek, ať výpadek jednoho nezhasne druhé:
  // objednávky a body spolu nesouvisí a čeká se na ně nezávisle.
  useEffect(() => {
    if (!customer) return;
    let alive = true;
    api
      .loyalty()
      .then((result) => {
        if (alive) setLoyalty(result.loyalty);
      })
      .catch(() => {
        if (alive) setLoyalty(null);
      });
    return () => {
      alive = false;
    };
  }, [customer]);

  if (loading && customer === undefined) {
    return (
      <div className="account-page narrow">
        <p className="muted">Načítám…</p>
      </div>
    );
  }

  // Nepřihlášený sem nepatří – pošleme ho na registraci.
  if (!customer) return <Navigate to="/registrace" replace />;

  async function onLogout() {
    await logout();
    navigate('/');
  }

  return (
    <div className="account-page">
      <header className="account-head">
        <div>
          <h1>Můj účet</h1>
          <p className="muted">{customer.email}</p>
        </div>
        <button type="button" className="button" onClick={onLogout}>
          Odhlásit se
        </button>
      </header>

      <section className="account-profile">
        <h2>Profil</h2>
        <dl className="track-facts">
          <div>
            <dt>E-mail</dt>
            <dd>{customer.email}</dd>
          </div>
          <div>
            <dt>Jméno</dt>
            <dd>{customer.fullName?.trim() || '—'}</dd>
          </div>
          <div>
            <dt>Telefon</dt>
            <dd>{customer.phone?.trim() || '—'}</dd>
          </div>
        </dl>
      </section>

      {/* VĚRNOSTNÍ BODY (#40). Zůstatek roste s každým zaplaceným nákupem
          a utrácí se v pokladně. Uplatněné body jsou způsobem platby té
          objednávky — při částečné vratce se nevracejí, a je poctivější to
          říct tady než až u reklamace. */}
      {loyalty !== null && (
        <section className="account-loyalty">
          <h2>Věrnostní body</h2>
          <p className="account-points">
            <strong>
              {loyalty.balancePoints} {plural(loyalty.balancePoints, 'bod', 'body', 'bodů')}
            </strong>{' '}
            <span className="muted">
              ({formatCzk(loyalty.balancePoints * loyalty.pointValueMinor)})
            </span>
          </p>
          <p className="muted">
            {loyalty.balancePoints > 0
              ? 'Uplatnit je můžete v pokladně — kolik z nich sedne na konkrétní košík, spočítáme až tam (body kryjí zboží, ne dopravu).'
              : 'Za každý zaplacený nákup vám body přibudou. 1 bod = 1,00 Kč.'}
          </p>
          <p className="muted small">
            Uplatněné body jsou způsobem platby objednávky. Při částečné vratce se nevracejí.
          </p>
        </section>
      )}

      {/* Odkaz vidí každý; kdo partner není, dostane na stránce vysvětlení
          a nabídku spolupráce místo cizích čísel. */}
      <section className="account-partner">
        <h2>Partnerský program</h2>
        <p className="muted">
          Doporučujete nás? <Link className="link" to="/partner">Přehled provizí</Link> ukáže váš
          odkaz, prokliky i to, kolik vám obchod dluží.
        </p>
      </section>

      <section className="account-orders" id="objednavky">
        <h2>Moje objednávky</h2>

        {orders === null && <p className="muted">Načítám objednávky…</p>}

        {ordersError !== null && (
          <p className="alert" role="alert">
            {ordersError}
          </p>
        )}

        {orders !== null && orders.length === 0 && ordersError === null && (
          <p className="muted">
            Zatím tu nic není. Až něco objednáte na e-mail <strong>{customer.email}</strong>,
            objeví se to tady.
          </p>
        )}

        {orders !== null && orders.length > 0 && (
          <ul className="account-order-list">
            {orders.map((order) => (
              <li key={order.orderNumber}>
                <Link
                  className="account-order"
                  to={`/objednavka?cislo=${encodeURIComponent(order.orderNumber)}&email=${encodeURIComponent(customer.email)}`}
                >
                  <span className="account-order-num">{order.orderNumber}</span>
                  <span className="muted small">{formatDate(order.placedAt)}</span>
                  <span className={`badge tone-${order.tone}`}>{order.statusLabel}</span>
                  <span className="account-order-meta muted small">
                    {order.itemCount} {itemWord(order.itemCount)}
                  </span>
                  <span className="account-order-total">{formatCzk(order.totalMinor)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium' }).format(new Date(iso));
}


